<script setup lang="ts">
import { z, ZodIssueCode } from 'zod'
import { Form } from '@primevue/forms'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import { updateAdminCompanyVideo } from '@swipe4work/api-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useToast } from '@/shared/composables/UseToast'
import { useCompanyStore, ViewCompanyType } from '@/shared/stores/CompanyStore'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'

const emits = defineEmits([ 'updateCompanyVideo' ])

const toast = useToast()
const queryClient = useQueryClient()

const { closeDialog, dialogData } = useDynamicDialogLifecycle<{
  companyId: number
}>()

const companyStore = useCompanyStore()

const company = ref<ViewCompanyType>()
const isSaving = ref(false)

onMounted(async () => {
  company.value = await companyStore.getCompany(dialogData.value.companyId!)
})

const hasVideo = computed(() => Boolean(company.value?.profile.videoUrl?.length))

const resolver = zodResolver(
  z.object({
    bunnyFileId: z.string().optional(),
    bunnyVideoThumbnailFilename: z.string().optional(),
  }).partial({
    bunnyFileId: true,
    bunnyVideoThumbnailFilename: true,
  }).superRefine((data, ctx) => {
    // A thumbnail only overrides the poster of an existing video, so it cannot stand on its own.
    if (!data.bunnyFileId && data.bunnyVideoThumbnailFilename) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: [ 'bunnyFileId' ],
        message: 'Vul een BunnyCDN Video ID in, of laat de thumbnail leeg',
      })
    }
  }),
)

async function onFormSubmit({ values, valid }: {
  values: any | undefined,
  valid: boolean
}) {
  if (!valid || !values) {
    toast.error('Validatiefout', 'Er zijn nog validatiefouten in het formulier')

    return
  }

  isSaving.value = true
  try {
    await updateAdminCompanyVideo(dialogData.value.companyId!, {
      bunnyCdnFileId: values.bunnyFileId || '',
      bunnyCdnVideoThumbnailFilename: values.bunnyVideoThumbnailFilename || '',
    })
    await queryClient.invalidateQueries({
      queryKey: [
        'admin',
        'admin-companies',
        'search',
      ],
    })
    toast.success('Bedrijfsvideo gewijzigd', 'De bedrijfsvideo is succesvol gewijzigd')
    closeDialog()
    emits('updateCompanyVideo')
  }
  catch {
    toast.error('Error', 'Er is iets misgegaan bij het wijzigen van de bedrijfsvideo')
  }
  finally {
    isSaving.value = false
  }
}
</script>

<template>
  <section class="min-w-90vw flex flex-col gap-y-7.5 lg:min-w-2xl md:min-w-xl">
    <h2 class="mt-5">
      Bedrijfsvideo wijzigen
    </h2>

    <Form
      v-if="company"
      :resolver
      @submit="onFormSubmit"
      class="w-full flex flex-col gap-4"
      :initial-values="{
        bunnyFileId: company.bunnyCdnFileId,
        bunnyVideoThumbnailFilename: company.bunnyCdnVideoThumbnailFilename,
      }"
    >
      <p class="text-sm text-surface-600">
        De video van <span class="font-medium">{{ company.name }}</span> wordt getoond op het
        bedrijfsprofiel in de app. Laat de thumbnail leeg om de thumbnail te gebruiken die BunnyCDN zelf
        van de video maakt — vul alleen een bestandsnaam in als er een eigen thumbnail is geüpload. Laat
        beide velden leeg om de video te verwijderen.
      </p>

      <div class="grid grid-cols-2 gap-x-2">
        <FormField
          v-slot="$field"
          name="bunnyFileId"
          as-child
          class="flex flex-col gap-1"
        >
          <div>
            <IftaLabel>
              <InputText class="w-full" />
              <label for="bunnyFileId">BunnyCDN Video ID</label>
            </IftaLabel>

            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </div>
        </FormField>

        <FormField
          v-slot="$field"
          name="bunnyVideoThumbnailFilename"
          class="flex flex-col gap-1"
        >
          <div>
            <IftaLabel>
              <InputText class="w-full" />
              <label for="bunnyVideoThumbnailFilename">BunnyCDN Video Thumbnail</label>
            </IftaLabel>

            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </div>
        </FormField>
      </div>

      <div v-if="hasVideo">
        <h3 class="mb-2">
          Huidige video
        </h3>
        <video
          :src="company.profile.videoUrl"
          :poster="company.profile.videoThumbnailUrl || undefined"
          controls
          preload="metadata"
          playsinline
          class="aspect-video w-full overflow-hidden rounded-lg bg-surface-900"
        />
      </div>

      <div class="flex justify-end gap-x-3">
        <Button
          label="Sluiten"
          severity="secondary"
          :disabled="isSaving"
          @click="closeDialog"
        />
        <Button
          type="submit"
          label="Opslaan"
          :loading="isSaving"
        />
      </div>
    </Form>
  </section>
</template>
